import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Button from '@/components/Button';
import BasicInput from '@/components/BasicInput';
import useCountdown from '@/hooks/useCountdown';
import { sendPhoneVerification, bindPhoneNumber } from '@/api/profile';
import { useToast } from '@/components/ToastProvider';
import { validatePhoneNumber } from '@/utils/validationUtils';
import { trackEvent } from '@/utils/googleAnalytics';
import useAuthStore from '@/stores/authStore';

// Import the phone icon
import changePhoneIcon from '@/assets/change_phone.svg';
import changeSuccessIcon from '@/assets/change_success.svg';

const BindPhoneDialog = ({ onClose, phone = null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { timeLeft, isActive, start } = useCountdown(60);
  const { user } = useAuthStore();

  const countryCode = import.meta.env.VITE_DEFAULT_COUNTRY_CODE || '81'; // Use env variable or default to Japan
  const [nationalNumber, setNationalNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerificationSent, setIsVerificationSent] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize with existing phone if provided
  useEffect(() => {
    if (phone) {
      // setNationalNumber(phone.nationalNumber);
    }
  }, [phone]);

  const validatePhone = () => {
    const newErrors = {};
    
    if (!nationalNumber) {
      newErrors.nationalNumber = t('user.errors.phone_required');
    } else if (!validatePhoneNumber(nationalNumber)) {
      newErrors.nationalNumber = t('user.errors.phone_invalid');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const validateVerificationCode = () => {
    const newErrors = {};
    
    if (!verificationCode) {
      newErrors.verificationCode = t('user.errors.verification_code_required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSendVerification = async () => {
    if (!validatePhone()) {
      return;
    }

    setIsLoading(true);
    try {
      const phoneData = {
        phone: {
          countryCode: parseInt(countryCode, 10),
          nationalNumber: nationalNumber
        }
      };

      await sendPhoneVerification(phoneData);
      setIsVerificationSent(true);
      start(); // Start countdown when verification code is sent
    } catch (error) {
      console.error('Phone verification request failed:', error);

      // Use toast value as error message if it exists, otherwise use current text value
      const errorMessage = error.response?.data?.toast ||
        (error.response?.data?.code === 'user_already_exists'
          ? t('user.errors.phone_already_exists')
          : t('user.errors.phone_send_failed'));

      setErrors({
        nationalNumber: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleVerifyAndBind = async () => {
    if (!validateVerificationCode()) {
      return;
    }
    
    setIsLoading(true);
    try {
      const bindData = {
        phone: {
          countryCode: parseInt(countryCode, 10),
          nationalNumber: nationalNumber
        },
        verificationCode: verificationCode
      };
      
      await bindPhoneNumber(bindData);
      setIsSuccess(true);
      
      // Invalidate profile data to refresh it
      queryClient.invalidateQueries(['user-profile']);
    } catch (error) {
      console.error('Phone binding failed:', error);
      setErrors({
        verificationCode: t('user.errors.verification_code_invalid')
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleResendCode = async () => {
    trackEvent('resend_code', {
      phone: `+${countryCode}${nationalNumber}`,
      email: user?.email || ''
    });
    setIsLoading(true);
    try {
      const phoneData = {
        phone: {
          countryCode: parseInt(countryCode, 10),
          nationalNumber: nationalNumber
        }
      };

      await sendPhoneVerification(phoneData);
      toast(t('user.phone.verification_resent'));
      start(); // Start countdown after resending
    } catch (error) {
      console.error('Resend verification failed:', error);

      // Use toast value as error message if it exists, otherwise use current text value
      const errorMessage = error.response?.data?.toast ||
        (error.response?.data?.code === 'verification_code_rate_limited'
          ? t('user.errors.verification_code_rate_limited')
          : error.response?.data?.code === 'user_already_exists'
          ? t('user.errors.phone_already_exists')
          : t('user.errors.verification_resend_failed'));

      toast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleChangePhoneNumber = () => {
    setIsVerificationSent(false);
  };
  
  const handleClose = () => {
    onClose();
  };

  const handleContactUsClick = () => {
    trackEvent('nocode_contact_us', {
      phone: `+${countryCode}${nationalNumber}`,
      email: user?.email || ''
    });
    navigate('/contact');
  };


  // Phone input view
  if (!isVerificationSent && !isSuccess) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="w-[360px] bg-white rounded-xl inline-flex flex-col justify-start items-center overflow-hidden">
          <div className="self-stretch px-6 pt-8 pb-6 flex flex-col justify-start items-center gap-4">
            <div className="w-12 h-12 relative">
              <img src={changePhoneIcon} alt="Phone" className="w-full h-full" />
            </div>
            <div className="self-stretch flex flex-col justify-center items-center gap-2">
              <div className="self-stretch text-center text-[#111111] text-lg font-bold leading-[27px]">
                {phone ? t('user.phone.change_title') : t('user.phone.bind_title')}
              </div>
              <div className="self-stretch text-center text-[#666666] text-sm font-normal leading-[21px]">
                {t('user.phone.bind_description')}
              </div>
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-1">
              <BasicInput
                placeholder={t('user.phone.number_placeholder')}
                value={nationalNumber}
                onChange={(e) => setNationalNumber(e.target.value)}
                error={errors.nationalNumber}
                containerClassName="w-full"
                inputClassName="pl-10"
                className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
              >
                <div className="absolute left-3 top-0 h-full flex items-center">
                  <span className="text-[#666666] text-sm font-normal">+{countryCode}</span>
                </div>
              </BasicInput>
            </div>
          </div>
          <div className="self-stretch px-6 pb-6 bg-white flex flex-col justify-center items-center">
            <div className="self-stretch flex items-center gap-3">
              <Button 
                variant="default" 
                className="flex-1 h-12 px-6 bg-white rounded-3xl outline outline-[0.5px] outline-offset-[-0.5px] outline-[#dddddd]"
                onClick={handleClose}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                variant="primary" 
                className="flex-1 h-12 px-6 bg-[#111111] rounded-3xl"
                onClick={handleSendVerification}
                disabled={isLoading}
              >
                {isLoading ? t('common.loading') : t('user.phone.send_verification')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Verification code view
  if (isVerificationSent && !isSuccess) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="w-[360px] bg-white rounded-xl inline-flex flex-col justify-start items-center overflow-hidden">
          <div className="self-stretch px-6 pt-8 pb-6 flex flex-col justify-start items-center gap-4">
            <div className="w-12 h-12 relative">
              <img src={changePhoneIcon} alt="Phone" className="w-full h-full" />
            </div>
            <div className="w-[263px] flex flex-col justify-center items-center gap-2">
              <div className="self-stretch text-center text-[#111111] text-lg font-bold leading-[27px]">
                {t('user.phone.verification_sent_title')}
              </div>
              <div className="self-stretch text-center justify-start">
                <span className="text-[#666666] text-sm font-normal leading-[21px]">
                  {t('user.phone.sent_to')} +{countryCode} {nationalNumber} 
                </span>
                <span 
                  className="text-[#2966ff] text-sm font-normal leading-[21px] cursor-pointer ml-1"
                  onClick={handleChangePhoneNumber}
                >
                  {t('user.phone.change')}
                </span>
              </div>
            </div>
            <div className="self-stretch flex flex-col justify-start items-start gap-1">
              <BasicInput
                placeholder={t('user.phone.verification_code_placeholder')}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                error={errors.verificationCode}
                containerClassName="self-stretch"
                className="bg-[#f7f7f7] rounded-md outline-1 outline-offset-[-1px] outline-[#dddddd]"
              />
              <div className="self-stretch pt-0.5 inline-flex justify-start items-center gap-1">
                <div>
                  {isActive ? (
                    <span className="text-[#aaaaaa] text-xs font-normal leading-[18px]">
                      {t('user.phone.no_code_countdown', { seconds: timeLeft })}
                    </span>
                  ) : (
                    <>
                      <span className="text-[#aaaaaa] text-xs font-normal leading-[18px]">
                        {t('user.phone.no_code')}
                      </span>
                      <span
                        className="text-[#2966ff] text-xs font-normal leading-[18px] cursor-pointer ml-1"
                        onClick={handleResendCode}
                      >
                        {t('user.phone.resend')}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="self-stretch px-6 py-4 bg-[#f7f7f7] rounded-md flex flex-col justify-start items-start gap-3">
              <div className="self-stretch text-[#333333] text-xs font-bold">
                {t('user.phone.help_title')}
              </div>
              <div className="self-stretch">
                <span className="text-[#999999] text-xs font-normal">
                  {t('user.phone.help_text')}
                </span>
                <span
                  className="text-[#2966ff] text-xs font-normal underline cursor-pointer"
                  onClick={handleContactUsClick}
                >
                  {t('user.phone.contact_us')}
                </span>
              </div>
            </div>
          </div>
          <div className="self-stretch px-6 pb-6 bg-white flex flex-col justify-center items-center">
            <div className="self-stretch flex items-center gap-3">
              <Button 
                variant="default" 
                className="flex-1 h-12 px-6 bg-white rounded-3xl outline outline-[0.5px] outline-offset-[-0.5px] outline-[#dddddd]"
                onClick={handleClose}
              >
                {t('common.cancel')}
              </Button>
              <Button 
                variant="primary" 
                className="flex-1 h-12 px-6 bg-[#111111] rounded-3xl"
                onClick={handleVerifyAndBind}
                disabled={isLoading}
              >
                {isLoading ? t('common.loading') : t('common.confirm')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Success view
  if (isSuccess) {
    return (
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
        <div className="w-[360px] bg-white rounded-xl inline-flex flex-col justify-start items-center overflow-hidden">
          <div className="self-stretch px-6 pt-8 pb-6 flex flex-col justify-start items-center gap-4">
            <div className="w-12 h-12 relative">
              <img src={changeSuccessIcon} alt="Success" className="w-full h-full" />
            </div>
            <div className="w-[263px] flex flex-col justify-center items-center gap-2">
              <div className="self-stretch text-center text-[#111111] text-lg font-bold leading-[27px]">
                {t('user.phone.success_title')}
              </div>
              <div className="self-stretch text-center text-[#666666] text-sm font-normal leading-[21px]">
                {t('user.phone.success_message', { phone: `+${countryCode} ${nationalNumber}` })}
              </div>
            </div>
          </div>
          <div className="px-6 pb-6 bg-white flex flex-col justify-center items-center">
            <div className="self-stretch inline-flex justify-center items-center">
              <Button 
                variant="primary" 
                className="w-[120px] h-12 px-6 bg-[#111111] rounded-3xl"
                onClick={handleClose}
              >
                {t('common.confirm')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default BindPhoneDialog; 